import { useCallback, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Extracts bucket and path from a Supabase storage URL.
 */
function parseStorageUrl(url: string): { bucket: string; path: string } | null {
    try {
        const match = url.match(/\/storage\/v1\/object\/(?:public|authenticated)\/([^/]+)\/(.+)/);
        if (match) {
            return { bucket: match[1], path: decodeURIComponent(match[2]) };
        }
    } catch {
        // ignore parse errors
    }
    return null;
}

const signedUrlCache = new Map<string, { url: string; expiresAt: number }>();
/** Requests still in flight, so two copies of one image share one round trip. */
const pendingSigns = new Map<string, Promise<string>>();
const EXPIRY_SECONDS = 3600;
const BUFFER_SECONDS = 300;

/**
 * Signs one storage URL and caches the result, sharing a request still in flight: a page that
 * mounts a section twice (the phone and desktop layouts of ContractDetail) would otherwise sign
 * every image in it twice.
 */
function signStorageUrl(storageUrl: string, bucket: string, path: string): Promise<string> {
    const pending = pendingSigns.get(storageUrl);
    if (pending) return pending;
    const request = supabase.storage
        .from(bucket)
        .createSignedUrl(path, EXPIRY_SECONDS)
        .then(({ data, error }) => {
            if (error || !data?.signedUrl) throw error ?? new Error('No signed URL returned');
            signedUrlCache.set(storageUrl, {
                url: data.signedUrl,
                expiresAt: Date.now() + (EXPIRY_SECONDS - BUFFER_SECONDS) * 1000,
            });
            return data.signedUrl;
        })
        .finally(() => { pendingSigns.delete(storageUrl); });
    pendingSigns.set(storageUrl, request);
    return request;
}

export type SignedUrlStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * React hook that converts a Supabase storage URL into a signed URL, and says whether it is
 * still signing or could not be signed, with a `retry` that drops the cached URL and signs
 * again. While it signs, `url` is the previous one, if any; a caller that must not show it reads
 * `status`. A caller that only needs the URL uses `useSignedUrl`.
 */
export function useSignedUrlState(storageUrl: string | null | undefined): {
    url: string | undefined;
    status: SignedUrlStatus;
    retry: () => void;
} {
    const [attempt, setAttempt] = useState(0);
    const [state, setState] = useState<{ url: string | undefined; status: SignedUrlStatus }>(() => {
        if (!storageUrl) return { url: undefined, status: 'idle' };
        if (!storageUrl.includes('/storage/v1/object/')) return { url: storageUrl, status: 'ready' };
        const cached = signedUrlCache.get(storageUrl);
        if (cached && Date.now() < cached.expiresAt) return { url: cached.url, status: 'ready' };
        return { url: undefined, status: 'loading' };
    });

    useEffect(() => {
        if (!storageUrl) { setState({ url: undefined, status: 'idle' }); return; }
        if (!storageUrl.includes('/storage/v1/object/')) { setState({ url: storageUrl, status: 'ready' }); return; }

        const cached = signedUrlCache.get(storageUrl);
        if (cached && Date.now() < cached.expiresAt) { setState({ url: cached.url, status: 'ready' }); return; }

        const parsed = parseStorageUrl(storageUrl);
        if (!parsed) { setState({ url: storageUrl, status: 'ready' }); return; }

        let cancelled = false;
        // The previous URL stays until the new one is signed, as it always has for `useSignedUrl`
        // callers (the driver's photo across a contract switch); only the status says it is stale.
        setState((prev) => (prev.status === 'loading' ? prev : { url: prev.url, status: 'loading' }));
        signStorageUrl(storageUrl, parsed.bucket, parsed.path).then(
            (url) => { if (!cancelled) setState({ url, status: 'ready' }); },
            () => { if (!cancelled) setState({ url: undefined, status: 'error' }); },
        );

        return () => { cancelled = true; };
    }, [storageUrl, attempt]);

    const retry = useCallback(() => {
        if (storageUrl) signedUrlCache.delete(storageUrl);
        setAttempt((n) => n + 1);
    }, [storageUrl]);

    return { url: state.url, status: state.status, retry };
}

/**
 * React hook that converts a Supabase storage URL into a signed URL.
 */
export function useSignedUrl(storageUrl: string | null | undefined): string | undefined {
    return useSignedUrlState(storageUrl).url;
}
